'use client';

import { useState, memo, useCallback } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { GlobeAltIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import TrustBadgesModal from '@/components/platform/modals/TrustBadgesModal';
import BetterStackModal from '@/components/platform/modals/BetterStackModal';

// Lazy load Mux Player (~300KB) - only loads on auth pages
// Memoize to prevent re-render when parent re-renders
const MuxPlayer = memo(
  dynamic(() => import('@mux/mux-player-react'), {
    ssr: false,
    loading: () => <div className="absolute inset-0 animate-pulse bg-gray-900" />,
  })
);
MuxPlayer.displayName = 'MuxPlayer';

// Stable style object — defined outside component to avoid breaking memo on MuxPlayer
const muxPlayerStyle = {
  '--media-object-fit': 'cover',
  '--media-object-position': 'center',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any;

function AuthVideoHero() {
  const { darkMode } = useDarkMode();
  const [trustModalOpen, setTrustModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  // Stable callback — useCallback prevents new reference on every render,
  // which would break memo() on MuxPlayer and cause video to reload
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleLoadedData = useCallback((e: any) => {
    const video = e.target as HTMLVideoElement;
    video.play().catch(() => {
      // Autoplay prevented - expected in Safari
    });
  }, []);

  return (
    <>
      <TrustBadgesModal
        isOpen={trustModalOpen}
        onClose={() => setTrustModalOpen(false)}
        darkMode={darkMode}
      />
      <BetterStackModal
        isOpen={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        darkMode={darkMode}
      />
      <div className="relative hidden w-0 flex-1 lg:block">
        {/* Video Background */}
        <div className="absolute inset-0 overflow-hidden">
          <MuxPlayer
            playbackId="J5ev88FFY021YU5frEGc6xh8FkTu7LoBbt3c00M0114vzk"
            loop
            muted
            autoPlay
            playsInline
            preload="auto"
            streamType="on-demand"
            className="absolute inset-0 h-full w-full"
            style={muxPlayerStyle}
            onLoadedData={handleLoadedData}
          />
          {/* Overlay matching background color */}
          <div
            className={`absolute inset-0 backdrop-blur ${darkMode ? 'bg-gray-900/25' : 'bg-white/25'}`}
            suppressHydrationWarning
          />
        </div>

        {/* Content */}
        <div
          className={`relative z-10 flex h-full flex-col items-start justify-center py-12 pr-12 pl-32 ${darkMode ? 'text-white' : 'text-gray-900'}`}
          suppressHydrationWarning
        >
          <div className="max-w-lg space-y-4">
            <div suppressHydrationWarning>
              {/* Valiyou Logo with TM - Black for light mode, white for dark mode */}
              <Image
                src="/Valiyou_black_TM.svg"
                alt="Valiyou"
                width={200}
                height={37}
                priority
                className="h-auto w-[200px] dark:hidden"
                style={{
                  filter:
                    'drop-shadow(0 4px 16px rgba(255, 255, 255, 0.8)) drop-shadow(0 2px 8px rgba(255, 255, 255, 0.6))',
                }}
                suppressHydrationWarning
              />
              <Image
                src="/Valiyou_white_TM.svg"
                alt="Valiyou"
                width={200}
                height={37}
                priority
                className="hidden h-auto w-[200px] dark:block"
                style={{
                  filter:
                    'drop-shadow(0 8px 32px rgba(0, 0, 0, 0.8)) drop-shadow(0 4px 16px rgba(0, 0, 0, 0.6))',
                }}
                suppressHydrationWarning
              />
              <p
                className="mt-4 text-3xl font-medium"
                suppressHydrationWarning
                style={{
                  textShadow: darkMode
                    ? '0 6px 24px rgba(0, 0, 0, 0.8), 0 3px 12px rgba(0, 0, 0, 0.6)'
                    : '0 3px 12px rgba(255, 255, 255, 0.8), 0 1px 6px rgba(255, 255, 255, 0.6)',
                }}
              >
                Turns data into clarity,
                <br />
                and clarity into value.
              </p>
            </div>

            <p
              className="text-xl leading-relaxed"
              suppressHydrationWarning
              style={{
                textShadow: darkMode
                  ? '0 4px 16px rgba(0, 0, 0, 0.8), 0 2px 8px rgba(0, 0, 0, 0.6)'
                  : '0 2px 8px rgba(255, 255, 255, 0.8), 0 1px 4px rgba(255, 255, 255, 0.6)',
              }}
            >
              The All-In-One Sponsorship & Marketing Value Platform
              <br />
              Measure, manage, and grow the value of your sponsorships with data, insights, and
              automation.
            </p>

            {/* Trust Badges */}
            <div className="mt-6 flex w-fit gap-x-3">
              {/* EU Hosting */}
              <button
                onClick={() => setTrustModalOpen(true)}
                className="flex cursor-pointer items-center gap-2 rounded bg-white/10 px-3 py-1.5 backdrop-blur-sm transition-colors hover:bg-white/20 dark:bg-black/20 dark:hover:bg-black/30"
              >
                <div className="flex-shrink-0">
                  <GlobeAltIcon
                    className={`h-5 w-5 ${darkMode ? 'text-white' : 'text-gray-900'}`}
                    suppressHydrationWarning
                  />
                </div>
                <div>
                  <span
                    className={`text-[11px] font-semibold whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}
                    suppressHydrationWarning
                  >
                    EU Hosting
                  </span>
                  <p
                    className={`text-[9px] whitespace-nowrap ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
                    suppressHydrationWarning
                  >
                    Data stored in EU
                  </p>
                </div>
              </button>

              {/* AES-256 Encryption */}
              <button
                onClick={() => setTrustModalOpen(true)}
                className="flex cursor-pointer items-center gap-2 rounded bg-white/10 px-3 py-1.5 backdrop-blur-sm transition-colors hover:bg-white/20 dark:bg-black/20 dark:hover:bg-black/30"
              >
                <div className="flex-shrink-0">
                  <LockClosedIcon
                    className={`h-5 w-5 ${darkMode ? 'text-white' : 'text-gray-900'}`}
                    suppressHydrationWarning
                  />
                </div>
                <div>
                  <span
                    className={`text-[11px] font-semibold whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}
                    suppressHydrationWarning
                  >
                    AES-256 Encryption
                  </span>
                  <p
                    className={`text-[9px] whitespace-nowrap ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
                    suppressHydrationWarning
                  >
                    Bank-level security
                  </p>
                </div>
              </button>
            </div>

            {/* Social Media Links */}
            <div className="mt-4 flex items-center gap-4">
              <span
                className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                suppressHydrationWarning
                style={{
                  textShadow: darkMode
                    ? '0 4px 16px rgba(0, 0, 0, 0.8), 0 2px 8px rgba(0, 0, 0, 0.6)'
                    : '0 2px 8px rgba(255, 255, 255, 0.8), 0 1px 4px rgba(255, 255, 255, 0.6)',
                }}
              >
                Follow us on:
              </span>
              <a
                href="https://www.crunchbase.com/organization/valiyou"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View us on Crunchbase"
                className="rounded-full p-2 transition-colors hover:bg-white/10 dark:hover:bg-black/20"
              >
                <svg
                  className={`h-6 w-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  fill="currentColor"
                  viewBox="0 0 32 32"
                  aria-hidden="true"
                  suppressHydrationWarning
                  style={{
                    filter: darkMode
                      ? 'drop-shadow(0 4px 16px rgba(0, 0, 0, 0.8))'
                      : 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.8))',
                  }}
                >
                  <path d="M23.707 16.68c0 0.017 0.001 0.037 0.001 0.057 0 1.452-1.177 2.629-2.629 2.629s-2.629-1.177-2.629-2.629c0-1.452 1.177-2.629 2.629-2.629 0.477 0 0.925 0.127 1.311 0.35l-0.013-0.007c0.784 0.451 1.309 1.277 1.331 2.226l0 0.003zM23.985 20.136c-0.376 0.325-0.815 0.592-1.294 0.777l-0.030 0.010c-0.494 0.197-1.066 0.312-1.664 0.312-0.96 0-1.851-0.294-2.588-0.797l0.016 0.010v0.475h-1.887v-13.209h1.874v5.136c0.634-0.434 1.403-0.719 2.233-0.786l0.017-0.001h0.337c0.002 0 0.005 0 0.007 0 2.533 0 4.586 2.053 4.586 4.586 0 1.393-0.621 2.64-1.601 3.481l-0.006 0.005zM9.808 19.080c0.321 0.15 0.697 0.237 1.093 0.237 1.060 0 1.974-0.625 2.393-1.527l0.007-0.016h2.074c-0.509 2.035-2.321 3.518-4.48 3.518-2.547 0-4.611-2.064-4.611-4.611s2.065-4.611 4.611-4.611c2.159 0 3.971 1.483 4.473 3.486l0.007 0.032h-2.074c-0.426-0.918-1.34-1.543-2.399-1.543-1.456 0-2.637 1.181-2.637 2.637 0 1.060 0.625 1.974 1.527 2.393l0.016 0.007zM27.997 1.004h-23.993c-1.654 0.007-2.992 1.346-2.999 2.998v23.994c0.007 1.654 1.346 2.992 2.998 2.999h23.994c1.654-0.007 2.992-1.346 2.999-2.998v-23.994c-0.007-1.654-1.346-2.992-2.998-2.999h-0.001z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/valiyou/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on LinkedIn"
                className="rounded-full p-2 transition-colors hover:bg-white/10 dark:hover:bg-black/20"
              >
                <svg
                  className={`h-6 w-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  suppressHydrationWarning
                  style={{
                    filter: darkMode
                      ? 'drop-shadow(0 4px 16px rgba(0, 0, 0, 0.8))'
                      : 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.8))',
                  }}
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com/valiyou.io"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Facebook"
                className="rounded-full p-2 transition-colors hover:bg-white/10 dark:hover:bg-black/20"
              >
                <svg
                  className={`h-7 w-7 ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  suppressHydrationWarning
                  style={{
                    filter: darkMode
                      ? 'drop-shadow(0 4px 16px rgba(0, 0, 0, 0.8))'
                      : 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.8))',
                  }}
                >
                  <path
                    fillRule="evenodd"
                    d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>

            {/* Better Stack Status Badge */}
            <div className="mt-4">
              <button
                onClick={() => setStatusModalOpen(true)}
                className="cursor-pointer transition-opacity hover:opacity-80"
              >
                <iframe
                  src={`https://status.valiyou.com/badge?theme=${darkMode ? 'dark' : 'light'}`}
                  width="250"
                  height="30"
                  frameBorder="0"
                  scrolling="no"
                  suppressHydrationWarning
                  style={{ colorScheme: 'normal', verticalAlign: 'middle', pointerEvents: 'none' }}
                  title="Valiyou Status"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Export memoized version to prevent re-renders when parent (AuthLayout) re-renders
export default memo(AuthVideoHero);
